import { useEffect, useRef, useState } from 'react';
import { Cloud, CloudUpload, CloudOff, TriangleAlert, RefreshCw } from 'lucide-react';
import { outbox, useOnline, useOutboxCounts } from '../offline/index.js';
import { syncState } from '../offline/syncState.js';
import { flushPhotos } from '../offline/photos.js';

// Field header, top right: one icon that answers "has my work reached the
// office?". Tap for the detail and a manual "Send now".
const ICON = { ok: Cloud, busy: CloudUpload, warn: CloudOff, bad: TriangleAlert };
// Short word under the icon, same colour as the icon.
const WORD = { ok: 'Connected', busy: 'Sending', warn: 'Disconnected', bad: 'Attention' };

export default function SyncIndicator() {
  const online = useOnline();
  const counts = useOutboxCounts();
  const state = syncState({ online, ...counts });
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const ref = useRef(null);
  const Icon = ICON[state.tone];

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const sendNow = async () => {
    setSending(true);
    try { await Promise.all([outbox.flush(), flushPhotos()]); } finally { setSending(false); }
  };

  return (
    <div className="sync-ind" ref={ref}>
      <button
        type="button"
        className={`icon-btn sync-ind-btn sync-ind-${state.tone}`}
        aria-label={state.label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sync-ind-icon">
          <Icon size={24} aria-hidden="true" />
          {state.count > 0 && <span className="sync-ind-count" aria-hidden="true">{state.count > 99 ? '99+' : state.count}</span>}
        </span>
        <span className="sync-ind-word" aria-hidden="true">{WORD[state.tone]}</span>
      </button>
      {open && (
        <div className="sync-pop" role="dialog" aria-label="Sync status">
          <p className={`sync-pop-title sync-pop-${state.tone}`}>{state.label}</p>
          <dl className="facts">
            <dt>Connection</dt><dd>{online ? 'Online' : 'Offline'}</dd>
            <dt>Waiting to send</dt><dd>{counts.pending}</dd>
            <dt>Need attention</dt><dd>{counts.rejected}</dd>
          </dl>
          {!online && <p className="muted small">Keep working. Everything is saved on this phone and sends when you have signal.</p>}
          {counts.pending > 0 && online && (
            <button type="button" className="btn primary" onClick={sendNow} disabled={sending}>
              <RefreshCw size={18} aria-hidden="true" className={sending ? 'spin' : undefined} />
              {sending ? 'Sending…' : 'Send now'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
